import { apiClient } from './client';

// Admin System Types
export interface SystemSettings {
  general: {
    site_name: string;
    site_description: string;
    support_email: string;
    support_phone: string;
    maintenance_mode: boolean;
    max_file_size: number;
    allowed_file_types: string[];
    session_timeout: number;
  };
  email: {
    smtp_host: string;
    smtp_port: number;
    smtp_user: string;
    smtp_password: string;
    smtp_use_tls: boolean;
    smtp_use_ssl: boolean;
    from_email: string;
    from_name: string;
  };
  security: {
    password_min_length: number;
    password_require_uppercase: boolean;
    password_require_lowercase: boolean;
    password_require_numbers: boolean;
    password_require_symbols: boolean;
    max_login_attempts: number;
    login_lockout_duration: number;
    session_security: boolean;
    two_factor_enabled: boolean;
  };
  maintenance: {
    auto_backup: boolean;
    backup_frequency: string;
    log_retention_days: number;
    cleanup_temp_files: boolean;
    database_optimization: boolean;
    system_monitoring: boolean;
  };
  api: {
    rate_limit_enabled: boolean;
    rate_limit_requests: number;
    rate_limit_window: number;
    api_timeout: number;
    cors_enabled: boolean;
    cors_origins: string[];
  };
  notifications: {
    email_notifications: boolean;
    sms_notifications: boolean;
    push_notifications: boolean;
    admin_alerts: boolean;
    user_notifications: boolean;
    notification_frequency: string;
  };
  voip: {
    provider: string;
    api_key: string;
    webhook_url: string;
    recording_enabled: boolean;
    quality_monitoring: boolean;
    max_call_duration: number;
  };
  system: {
    timezone: string;
    language: string;
    date_format: string;
    currency: string;
    logs_enabled: boolean;
    debug_mode: boolean;
  };
}

export interface SystemSettingsUpdate {
  [key: string]: Partial<SystemSettings[keyof SystemSettings]>;
}

export interface SystemHealth {
  status: 'online' | 'offline' | 'maintenance' | 'degraded';
  database: {
    status: string;
    connection_count: number;
    query_time_avg: number;
    last_check: string;
  };
  cache: {
    status: string;
    hit_rate: number;
    memory_usage: number;
    last_check: string;
  };
  storage: {
    status: string;
    total_space: number;
    used_space: number;
    free_space: number;
    last_check: string;
  };
  services: {
    status: string;
    active_services: number;
    failed_services: number;
    last_check: string;
  };
}

export interface SystemMetrics {
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  network_usage: number;
  active_users: number;
  total_requests: number;
  error_rate: number;
  response_time: number;
  timestamp: string;
}

export interface ActivityLog {
  id: number;
  user_id: number;
  user_name: string;
  action: string;
  resource: string;
  details: string;
  ip_address: string;
  user_agent: string;
  timestamp: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
}

export interface ActivityLogList {
  logs: ActivityLog[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface BackupStatus {
  last_backup: string;
  next_backup: string;
  backup_size: number;
  status: 'success' | 'failed' | 'running' | 'pending';
  retention_days: number;
  auto_backup: boolean;
}

export interface NotificationTemplate {
  id: number;
  name: string;
  type: 'email' | 'sms' | 'push';
  subject?: string;
  content: string;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

// Helper to coerce backend list-based settings into structured object
const buildDefaultSettings = (): SystemSettings => ({
  general: {
    site_name: '',
    site_description: '',
    support_email: '',
    support_phone: '',
    maintenance_mode: false,
    max_file_size: 10 * 1024 * 1024,
    allowed_file_types: [],
    session_timeout: 60,
  },
  email: {
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    from_email: '',
    from_name: '',
  },
  security: {
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_lowercase: true,
    password_require_numbers: true,
    password_require_symbols: false,
    max_login_attempts: 5,
    login_lockout_duration: 15,
    session_security: true,
    two_factor_enabled: false,
  },
  maintenance: {
    auto_backup: true,
    backup_frequency: 'daily',
    log_retention_days: 30,
    cleanup_temp_files: true,
    database_optimization: true,
    system_monitoring: true,
  },
  api: {
    rate_limit_enabled: false,
    rate_limit_requests: 100,
    rate_limit_window: 60,
    api_timeout: 30,
    cors_enabled: true,
    cors_origins: ['http://localhost:3000'],
  },
  notifications: {
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    admin_alerts: true,
    user_notifications: true,
    notification_frequency: 'immediate',
  },
  voip: {
    provider: '',
    api_key: '',
    webhook_url: '',
    recording_enabled: false,
    quality_monitoring: false,
    max_call_duration: 3600,
  },
  system: {
    timezone: 'Europe/Istanbul',
    language: 'tr',
    date_format: 'dd.MM.yyyy',
    currency: 'EUR',
    logs_enabled: true,
    debug_mode: false,
  },
});

const coerceSettings = (data: any): SystemSettings => {
  // If already structured, return as-is
  if (data && typeof data === 'object' && 'general' in data) return data as SystemSettings;

  const settings = buildDefaultSettings();
  if (!Array.isArray(data)) return settings;

  for (const item of data) {
    const key = item?.key;
    const val = item?.value;
    switch (key) {
      case 'max_upload_size':
        settings.general.max_file_size = Number(val) || settings.general.max_file_size;
        break;
      case 'session_timeout':
        settings.general.session_timeout = Math.floor(Number(val)) || settings.general.session_timeout;
        break;
      case 'maintenance_mode':
        settings.general.maintenance_mode = String(val).toLowerCase() === 'true';
        break;
      default:
        // Unknown keys can be mapped later
        break;
    }
  }
  return settings;
};

// Admin API Service
export const adminApi = {
  // System Settings
  settings: {
    // Get current system settings
    async get(): Promise<SystemSettings> {
      try {
        const response = await apiClient.get('/api/v1/admin/settings');
        return coerceSettings(response.data);
      } catch (error) {
        console.error('Error fetching system settings:', error);
        throw new Error('Sistem ayarları getirilemedi');
      }
    },

    // Update system settings
    async update(settings: SystemSettingsUpdate): Promise<SystemSettings> {
      try {
        // NOTE: Backend güncel hali tek anahtar güncelliyor; burada şimdilik no-op/echo davranışı sağlıyoruz
        return coerceSettings(settings);
      } catch (error: any) {
        console.error('Error updating system settings:', error);
        const message = error.response?.data?.detail || 'Sistem ayarları güncellenemedi';
        throw new Error(message);
      }
    },

    // Test email settings
    async testEmail(toEmail: string): Promise<{ success: boolean; message: string }> {
      try {
        const response = await apiClient.post('/api/v1/admin/settings/test-email', {
          to_email: toEmail
        });
        return response.data;
      } catch (error: any) {
        console.error('Error testing email settings:', error);
        const message = error.response?.data?.detail || 'E-posta testi başarısız';
        throw new Error(message);
      }
    }
  },

  // System Health & Monitoring
  health: {
    // Get system health status
    async get(): Promise<SystemHealth> {
      try {
        const response = await apiClient.get('/api/v1/admin/health');
        return response.data;
      } catch (error) {
        console.error('Error fetching system health:', error);
        throw new Error('Sistem durumu getirilemedi');
      }
    },

    // Get system metrics
    async getMetrics(): Promise<SystemMetrics> {
      try {
        const response = await apiClient.get('/api/v1/admin/metrics');
        return response.data;
      } catch (error) {
        console.error('Error fetching system metrics:', error);
        throw new Error('Sistem metrikleri getirilemedi');
      }
    }
  },

  // Activity Logs
  logs: {
    // Get activity logs
    async list(params: any = {}): Promise<ActivityLogList> {
      try {
        const response = await apiClient.get('/api/v1/admin/logs', { params });
        return response.data;
      } catch (error) {
        console.error('Error fetching activity logs:', error);
        throw new Error('Aktivite logları getirilemedi');
      }
    },

    // Clear old logs
    async cleanup(days: number): Promise<{ deleted_count: number }> {
      try {
        const response = await apiClient.delete(`/api/v1/admin/logs/cleanup/${days}`);
        return response.data;
      } catch (error) {
        console.error('Error cleaning up logs:', error);
        throw new Error('Log temizleme başarısız');
      }
    }
  },

  // Backup Management
  backup: {
    // Get backup status
    async getStatus(): Promise<BackupStatus> {
      try {
        const response = await apiClient.get('/api/v1/admin/backup/status');
        return response.data;
      } catch (error) {
        console.error('Error fetching backup status:', error);
        throw new Error('Yedekleme durumu getirilemedi');
      }
    },

    // Create manual backup
    async create(): Promise<{ backup_id: string; status: string }> {
      try {
        const response = await apiClient.post('/api/v1/admin/backup/create');
        return response.data;
      } catch (error) {
        console.error('Error creating backup:', error);
        throw new Error('Yedekleme oluşturulamadı');
      }
    },

    // Restore backup
    async restore(backupId: string): Promise<{ status: string; message: string }> {
      try {
        const response = await apiClient.post(`/api/v1/admin/backup/restore/${backupId}`);
        return response.data;
      } catch (error) {
        console.error('Error restoring backup:', error);
        throw new Error('Yedek geri yükleme başarısız');
      }
    }
  },

  // Notification Templates
  notifications: {
    // List notification templates
    async list(): Promise<NotificationTemplate[]> {
      try {
        const response = await apiClient.get('/api/v1/admin/notifications/templates');
        return response.data.templates || [];
      } catch (error) {
        console.error('Error fetching notification templates:', error);
        throw new Error('Bildirim şablonları getirilemedi');
      }
    },

    // Create notification template
    async create(template: Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<NotificationTemplate> {
      try {
        const response = await apiClient.post('/api/v1/admin/notifications/templates', template);
        return response.data;
      } catch (error) {
        console.error('Error creating notification template:', error);
        throw new Error('Bildirim şablonu oluşturulamadı');
      }
    },

    // Update notification template
    async update(id: number, template: Partial<NotificationTemplate>): Promise<NotificationTemplate> {
      try {
        const response = await apiClient.put(`/api/v1/admin/notifications/templates/${id}`, template);
        return response.data;
      } catch (error) {
        console.error('Error updating notification template:', error);
        throw new Error('Bildirim şablonu güncellenemedi');
      }
    },

    // Delete notification template
    async delete(id: number): Promise<void> {
      try {
        await apiClient.delete(`/api/v1/admin/notifications/templates/${id}`);
      } catch (error) {
        console.error('Error deleting notification template:', error);
        throw new Error('Bildirim şablonu silinemedi');
      }
    }
  },

  // System Maintenance
  maintenance: {
    // Enable/disable maintenance mode
    async toggleMaintenanceMode(enabled: boolean): Promise<{ status: boolean; message: string }> {
      try {
        const response = await apiClient.post('/api/v1/admin/maintenance', { enabled });
        return response.data;
      } catch (error) {
        console.error('Error toggling maintenance mode:', error);
        throw new Error('Bakım modu değiştirilemedi');
      }
    },

    // Clear application cache
    async clearCache(): Promise<{ success: boolean; message: string }> {
      try {
        const response = await apiClient.post('/api/v1/admin/cache/clear');
        return response.data;
      } catch (error) {
        console.error('Error clearing cache:', error);
        throw new Error('Önbellek temizlenemedi');
      }
    }
  }
}; 